import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import type { HolandaChannel, HolandaTicketBrief } from "@/api/types";
import {
  cartCount,
  CartIcons,
  cellMeta,
  fmtDur,
  groupAgencies,
  isAlert,
  Legend,
  padronTexto,
  STATE_META,
  TicketActions,
} from "@/components/holanda-shared";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useHolandaOverview } from "@/hooks/useHolanda";

function PositionCard({
  deviceGid,
  ch,
  ticket,
  siteName,
}: {
  deviceGid: string;
  ch: HolandaChannel;
  ticket: HolandaTicketBrief | null;
  siteName: string;
}) {
  const meta = cellMeta(ch);
  // En activo los íconos de carrito ya muestran el conteo → no repetir "2 carritos".
  const label =
    !isAlert(ch.state) && cartCount(ch) > 0 ? "Enfriando" : meta.label;
  // Limpiado = ya atendido: no mostrar la descripción de urgencia ni los botones.
  const desc =
    isAlert(ch.state) && !ch.cleared ? STATE_META[ch.state].desc : undefined;
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center gap-2">
          <div
            className={`flex size-9 shrink-0 items-center justify-center rounded text-sm font-semibold text-white/90 ${meta.cell}`}
          >
            {ch.channel_num}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CartIcons ch={ch} />
              <span>{label}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {Math.round(ch.watts)} W · {fmtDur(ch.minutes_in_state)}
            </div>
          </div>
        </div>
        {desc && <div className="text-xs text-muted-foreground">{desc}</div>}
        {padronTexto(ch) && (
          <div
            className={`text-[11px] ${ch.coincide_padron === false ? "font-medium text-amber-600" : "text-muted-foreground"}`}
          >
            {ch.coincide_padron === false ? "⚠ " : ""}
            {padronTexto(ch)}
          </div>
        )}
        {ch.label && (
          <div
            className="truncate text-[11px] text-muted-foreground"
            title={ch.label}
          >
            {ch.label}
          </div>
        )}
        {isAlert(ch.state) && !ch.cleared && (
          <TicketActions
            deviceGid={deviceGid}
            channelNum={ch.channel_num}
            ticket={ticket}
            siteName={siteName}
            label={ch.label}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function HolandaAgencyPage() {
  const { agencyCode } = useParams();
  const code = decodeURIComponent(agencyCode ?? "");
  const { data, isPending, error, isError } = useHolandaOverview();

  const agency = data
    ? groupAgencies(data.sites).find((a) => a.code === code)
    : undefined;
  // Ticket abierto por canal (la cola de alertas del overview lo trae).
  const ticketByKey = new Map<string, HolandaTicketBrief | null>(
    (data?.alerts ?? []).map((a) => [
      `${a.device_gid}-${a.channel_num}`,
      a.ticket,
    ]),
  );

  const errorMsg = isError
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar el estado de los carritos."
    : null;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link
          to="/holanda/carritos"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Volver
        </Link>
        {agency && <h1 className="text-xl font-semibold">{agency.name}</h1>}
      </div>

      {errorMsg && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {errorMsg}
        </div>
      )}

      {isPending && !data && (
        <div className="text-sm text-muted-foreground">Cargando…</div>
      )}

      {data && !agency && (
        <div className="text-sm text-muted-foreground">
          Agencia no encontrada.{" "}
          <Link to="/holanda/carritos" className="underline">
            Volver al tablero
          </Link>
          .
        </div>
      )}

      {agency && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {agency.sites.length} tablero(s) · {agency.carts} carrito(s)
            </span>
            {agency.alarms > 0 && (
              <Badge className="bg-red-600 text-white">
                {agency.alarms} alarma
              </Badge>
            )}
            {agency.watching > 0 && (
              <Badge className="bg-amber-400 text-black">
                {agency.watching} vig.
              </Badge>
            )}
            <div className="ml-auto">
              <Legend />
            </div>
          </div>

          {agency.sites.map((s) => {
            const tablero =
              s.site_name.replace(/^\s*\d{3,4}[\s-]*/, "").trim() ||
              s.site_name;
            return (
              <section key={s.device_gid} className="space-y-2">
                <h2 className="border-b pb-1 text-sm font-semibold">
                  {tablero}
                </h2>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {s.channels.map((ch) => (
                    <PositionCard
                      key={ch.channel_num}
                      deviceGid={s.device_gid}
                      siteName={s.site_name}
                      ch={ch}
                      ticket={
                        ticketByKey.get(`${s.device_gid}-${ch.channel_num}`) ??
                        null
                      }
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
